import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { CircularProgress, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActions,
  RowActionsExtra,
  Icons,
} from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";

import { Select } from "../../../../../../../../../components/Form";
import { Color, Colors } from "../../../../../../../../../components/Form/Button";
import { ActionButtonProps } from "../../../../../../../../../components/Generic/ActionButtons";
import ConfirmChangeStatus from "../../../../../../../../../components/Generic/ConfirmChangeStatusModal";
import Modal from "../../../../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../../context/StateContext";
import useWarehouseContext from "../../../../../../../../../hooks/useWarehouseContext";
import { Error } from "../../../../../../../../../models/error";
import {
  Filters,
  Row,
  Searches,
  Sort,
  Sorts,
} from "../../../../../../../../../models/shipeezi.table";
import { Warehouse } from "../../../../../../../../../models/warehouse";
import { AxisConfig, WarehouseMap } from "../../../../../../../../../models/warehouse.map";
import WarehousesMapsService from "../../../../../../../../../services/warehouse.map.service";
import { checkError } from "../../../../../../../../../util/checkError";
import { Status } from "../../../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../../../util/onSaveItemSelected";
import ConfirmDeleteWarehouseMapModal, {
  DeleteWarehouseMapHandle,
} from "../../../Maps/ConfirmDeleteWarehouseMapModal";
import { WarehouseMapPageModal } from "../../../Maps/WarehouseMapPageModal";
import { Wrapper, Header, Main, Icon, IconStatus, ChangeStatusButton, BadgeStatus } from "./styles";

enum MapProperties {
  SECTION = "section",
  WAREHOUSE = "warehouse",
  CONFIG_COLUMN = "configColumn",
  CONFIG_ROW = "configRow",
  CONFIG_LEVEL = "configLevel",
}

interface MapsModalProps {}

export interface MapsModalHandle {
  setOpenModal: (warehouse: Warehouse) => void;
  setCloseModal: () => void;
}

const MapsModal: ForwardRefRenderFunction<MapsModalHandle, MapsModalProps> = (props, ref) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const deleteMapRef = useRef<DeleteWarehouseMapHandle>(null);
  const {
    warehouse,
    setWarehouse,
    sectionId,
    setSectionId,
    searches,
    setSearches,
    setSorting,
    page,
    setPage,
    pageSize,
    setPageSize,
    filters,
    setFilters,
    warehousesMap,
    isFetchingWarehouseMaps,
    getAllWarehouseMaps,
    selectedMap,
    setSelectedMap,
  } = useWarehouseContext();

  const [open, setOpen] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);
  const [loadedLocationsMaps, setLoadedLocationsMaps] = useState<WarehouseMap[]>([]);

  const [rows, setRows] = useState<Row[]>([]);

  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [isCreatingNewMap, setIsCreatingNewMap] = useState<boolean>(false);
  const [openMapModal, setOpenMapModal] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>();
  const [hasChangedMapCell, setHasChangedMapCell] = useState<boolean>(false);

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => {
      switch (sort.columnName) {
        case MapProperties.CONFIG_COLUMN:
          sorting.push([MapProperties.CONFIG_COLUMN, "size", sort.direction]);
          break;
        case MapProperties.CONFIG_ROW:
          sorting.push([MapProperties.CONFIG_ROW, "size", sort.direction]);
          break;
        case MapProperties.CONFIG_LEVEL:
          sorting.push([MapProperties.CONFIG_LEVEL, "size", sort.direction]);
          break;
        default:
          sorting.push([sort.columnName, sort.direction]);
      }
    });

    return sorting;
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleFilter = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  const handleGetDefaultFilters = () => Object.values(filters)?.[0];

  const {
    mutate: onCreateMap,
    isLoading: isCreating,
    isSuccess: isCreatingSuccess,
    isError: isCreatingError,
    error: createError,
  } = useMutation(["warehouse.map.create"], (warehouseMap: WarehouseMap) =>
    WarehousesMapsService.create({ ...warehouseMap, companyId: currentUser?.companyId, sectionId })
  );

  const {
    mutate: onUpdateMap,
    isLoading: isUpdating,
    isSuccess: isUpdatingSuccess,
    isError: isUpdatingError,
    error: updatingError,
    variables: updateVariables,
  } = useMutation(
    ["warehouse.map.update"],
    ({ id, warehouseMap }: { id: number; warehouseMap: WarehouseMap }) =>
      WarehousesMapsService.update(id, warehouseMap)
  );

  const updateMap = (
    { id, warehouseMap }: { id: number; warehouseMap: WarehouseMap },
    callback: () => void
  ) => {
    return onUpdateMap(
      { id, warehouseMap },
      {
        onSuccess: () => {
          callback();
        },
        onError: () => callback(),
      }
    );
  };

  const { data: responseCount } = useQuery(
    ["get.count.by.warehosue.id", warehouse, searches, filters],
    () =>
      WarehousesMapsService.count({
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          { association: "section" },
          {
            association: "warehouse",
            where: {
              id: warehouse?.id,
            },
          },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId && !!warehouse,
      refetchOnWindowFocus: false,
    }
  );

  const setOpenModal = (warehouse: Warehouse) => {
    setWarehouse(warehouse);
    setOpen(true);

    if (warehouse.sections && warehouse.sections.length > 0) {
      const findSectionDefault = warehouse.sections.find((section) => section.isDefault);

      if (findSectionDefault) setSectionId(findSectionDefault.id);
      else setSectionId(warehouse.sections[0]?.id);
    }
  };

  const setCloseModal = () => {
    setOpen(false);
    setWarehouse(undefined);
  };

  const onEditMap = (_id: number | string, row: WarehouseMap) => {
    setSelectedMap(row);
    setIsOnViewMode(false);
    setOpenMapModal(true);
  };

  const onViewMap = (_id: number | string, row: WarehouseMap) => {
    setSelectedMap(row);
    setIsOnViewMode(true);
    setOpenMapModal(true);
  };

  const isMapAlreadyInUse = (map?: WarehouseMap) => {
    return !map?.mapCells?.filter(
      (cell) =>
        (cell.storageBoxes && cell.storageBoxes.length > 0) ||
        (cell.itemsJobs && cell.itemsJobs.length > 0)
    )?.length;
  };

  const onDeleteMap = (id: number | string) => {
    const DeleteIcon = Icon(FiTrash2);
    const map = warehousesMap?.find((item) => item.id === Number(id));

    if (!map) return null;

    if (!isMapAlreadyInUse(map)) return null;

    if (isUpdating && updateVariables?.id === Number(id)) {
      return <CircularProgress size={18} />;
    }

    return (
      <Tooltip title={translate("delete")} placement="top">
        <div>
          <DeleteIcon
            size={18}
            color="#f04438"
            onClick={() => {
              deleteMapRef.current?.onOpenModal(map);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const onDelete = (map?: WarehouseMap) => {
    if (map) {
      updateMap(
        {
          id: Number(map.id),
          warehouseMap: {
            ...map,
            valid: false,
          },
        },
        () => {
          deleteMapRef.current?.onCloseModal();
        }
      );
    }
  };

  const onChangeStatus = (index: number | string, row: WarehouseMap) => {
    const map = warehousesMap?.find((item) => item.id === Number(index));

    if (!isMapAlreadyInUse(map)) return null;

    if (isUpdating && updateVariables?.id === Number(index)) {
      return <CircularProgress size={18} />;
    }

    return (
      <Tooltip title={translate("status")} placement="top">
        <div>
          <IconStatus
            onClick={() => {
              onUpdateMap({
                id: Number(index),
                warehouseMap: { ...row, isActive: !row?.isActive } as WarehouseMap,
              });
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0 && rows.length !== loadedLocationsMaps.length) {
      return (
        <ChangeStatusButton
          tooltip={translate("changeStatus")}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <Icons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </ChangeStatusButton>
      );
    }
  };

  const onSaveSelectedMaps = (rowsArr: number[]) => {
    const maps = warehousesMap?.map((map) => ({
      ...map,
      status: map?.isActive ? Status.ACTIVE : Status.INACTIVE,
    }));

    const rowsSelected = onSaveItemSelected(rowsArr, rows, maps);

    const selectedMaps = [...rowsSelected] as WarehouseMap[];
    const selectedLoadedLocationsMaps: WarehouseMap[] = selectedMaps.filter(
      (map) => !isMapAlreadyInUse(map)
    );

    setLoadedLocationsMaps(selectedLoadedLocationsMaps);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    { name: "description", title: translate("description") },
    {
      name: "configRow",
      title: translate("rows"),
      hideSearch: true,
      formatComponent: (rows: AxisConfig) => rows && rows.size && rows.size,
    },
    {
      name: "configColumn",
      title: translate("columns"),
      formatComponent: (configColumn: AxisConfig) =>
        configColumn && configColumn?.size && configColumn.size,
      hideSearch: true,
    },
    {
      name: "configLevel",
      title: translate("levels"),
      formatComponent: (configLevel: AxisConfig) =>
        configLevel && configLevel?.size && configLevel.size,
      hideSearch: true,
    },
    {
      name: "isActive",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (status: boolean) => (
        <BadgeStatus isActive={status}>{status ? Status.ACTIVE : Status.INACTIVE}</BadgeStatus>
      ),
    },
  ];

  const actionButtonProps: ActionButtonProps = {
    dataCyCancel: "cancelButton",
    textCancel: translate("close"),
    customColorCancel: Color.DARK,
    onCancel: setCloseModal,
  };

  const generalActions: GeneralActions = {
    orderBy: {
      right: ["handleChangeStatus", "handleNewRecord"],
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleNewRecord: {
      text: translate("newMap"),
      action: () => {
        setOpenMapModal(true);
        setIsOnViewMode(false);
        setIsCreatingNewMap(true);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          optionsSelected: handleGetDefaultFilters(),
          column: "isActive",
          options: [
            { value: "1", label: translate("active") },
            { value: "0", label: translate("inactive") },
          ],
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderChangeAllStatus(),
  };

  const rowActions: RowActions = {
    orderBy: ["handleEdit", "handleDetails", "onStatus", "onDelete"],
    handleEdit: onEditMap,
    handleDetails: onViewMap,
  };

  const rowActionsExtra: RowActionsExtra[] = [
    { title: translate("delete"), key: "onDelete", buttonAndAction: onDeleteMap },
    { title: translate("status"), key: "onStatus", buttonAndAction: onChangeStatus },
  ];

  useImperativeHandle(ref, () => ({
    setOpenModal,
    setCloseModal,
  }));

  useCallback(() => {
    const error: AxiosError<Error> = (createError || updatingError) as AxiosError<Error>;

    checkError(error, translate("anErrorOccurredTryAgain"), "error-occurred-creating-updating-map");
    setHasError(true);
  }, [createError, updatingError, isCreatingError, isUpdatingError]);

  useEffect(() => {
    if (isCreatingSuccess || isUpdatingSuccess) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "create.or.update.notification.success",
      });
      setHasError(false);
      getAllWarehouseMaps();
    }
  }, [isCreatingSuccess, isUpdatingSuccess]);

  useEffect(() => {
    if (!hasError) {
      setClearSelection(false);
      setRows([]);
    }
  }, [hasError]);

  const handleWarnLoadedLocationsMaps = (loadedLocationsMaps: WarehouseMap[]) => {
    const loadedLocationsMapsNames = loadedLocationsMaps
      .map((loadedMap) => loadedMap.name)
      .join(", ");

    return NotificationToast.warning(
      `${translate(
        "theFollowingMapsHaveLoadedLocationsAndCanNotChangeStatus"
      )} ${loadedLocationsMapsNames}`,
      { id: "notification.warning.loaded.location.maps" }
    );
  };

  const getUnloadedLocationsMaps = (selectedMaps: WarehouseMap[]) => {
    return selectedMaps.filter((map) => isMapAlreadyInUse(map));
  };

  return (
    <Modal
      open={open}
      handleClose={setCloseModal}
      showActionButtons
      actionButtonProps={actionButtonProps}
      title={`${warehouse?.name} ${translate("maps")}`}
    >
      <Wrapper>
        <Header>
          <Select
            value={sectionId}
            onChange={(event: SelectChangeEvent<any>) => setSectionId(event.target.value)}
            label={translate("section")}
          >
            {warehouse?.sections?.map((section) => (
              <MenuItem key={section.id} value={section.id}>
                {section.name}
              </MenuItem>
            ))}
          </Select>
        </Header>
        <Main>
          <ShipeeziNewGrid
            data={warehousesMap ?? []}
            columns={columns}
            totalItems={responseCount?.data}
            loadingData={isFetchingWarehouseMaps}
            emptyData={translate("noInformationToBeShown")}
            pageSize={pageSize}
            pageSizeList={[10, 20, 60, 100]}
            currentPage={page}
            onChangePageSize={setPageSize}
            onNextPage={handleCurrentPage}
            onPreviousPage={handleCurrentPage}
            onGoToPage={handleCurrentPage}
            onSorting={handleSorting}
            clearSelection={clearSelection}
            handleSelect={(rows: number[]) => onSaveSelectedMaps(rows)}
            generalActions={generalActions}
            rowActions={rowActions}
            rowActionsExtra={rowActionsExtra}
            positionActionsColumn="right"
          />
        </Main>
        {openMapModal && (
          <WarehouseMapPageModal
            open={openMapModal}
            closeModal={() => {
              if (hasChangedMapCell) getAllWarehouseMaps();
              setOpenMapModal(false);
              setSelectedMap(undefined);
              setHasError(undefined);
              setHasChangedMapCell(false);
              setIsCreatingNewMap(false);
            }}
            isOnViewMode={isOnViewMode}
            setHasChangedMapCell={setHasChangedMapCell}
            selectedWarehouseMap={selectedMap}
            createWarehouseMap={(map: WarehouseMap) => {
              if (sectionId) {
                onCreateMap(map);
              }
            }}
            updateWarehouseMap={(map: WarehouseMap, mapId: number) => {
              onUpdateMap({
                id: mapId,
                warehouseMap: map,
              });
            }}
            isLoading={isCreating || isUpdating}
            hasError={hasError}
            isCreatingNewMap={isCreatingNewMap}
          />
        )}

        <ConfirmDeleteWarehouseMapModal
          ref={deleteMapRef}
          onDelete={onDelete}
          isDeleting={isUpdating}
          hasError={hasError}
        />

        {openConfirmation && (
          <ConfirmChangeStatus
            open={openConfirmation}
            rows={getUnloadedLocationsMaps(rows as WarehouseMap[])}
            isLoading={isUpdating}
            hasErrors={hasError}
            closeModal={() => setOpenConfirmation(false)}
            update={(rowId, status) => {
              if (rowId) {
                const map = warehousesMap?.find((row) => row.id === rowId);

                onUpdateMap({
                  id: rowId,
                  warehouseMap: { ...map, isActive: status === Status.ACTIVE } as WarehouseMap,
                });
              }

              if (loadedLocationsMaps.length > 0) {
                handleWarnLoadedLocationsMaps(loadedLocationsMaps);
              }

              setClearSelection(true);
            }}
          />
        )}
      </Wrapper>
    </Modal>
  );
};

export default forwardRef(MapsModal);
